import { useMemo } from 'react';
import { useQuery } from 'urql';
import PageHeader from '@/components/common/PageHeader.jsx';
import StickyActions from '@/components/common/StickyActions.jsx';
import EmptyState from '@/components/common/EmptyState.jsx';
import { ATENCION_DASHBOARD } from './gql.js';

export default function DashboardList({ filters, onChangeFilters, onSelect }) {
  const [result] = useQuery({
    query: ATENCION_DASHBOARD,
    variables: filters,
  });

  const data = result.data?.atencionDashboard;

  const kpis = useMemo(() => (
    data
      ? [
          {
            label: 'Reclamos abiertos',
            value: data.reclamosAbiertos,
            description: 'Tickets activos hoy',
          },
          {
            label: 'MTTA promedio',
            value: data.mttaPromedio ? `${Math.round(data.mttaPromedio)} min` : '—',
            description: 'Tiempo medio a primera respuesta',
          },
          {
            label: 'MTTR promedio',
            value: data.mttrPromedio ? `${Math.round(data.mttrPromedio)} min` : '—',
            description: 'Tiempo medio de resolución',
          },
          {
            label: '% SLA cumplido',
            value: data.slaCumplido != null ? `${Math.round(data.slaCumplido * 100)}%` : '—',
            description: 'Cumplimiento de promesas activas',
          },
        ]
      : []
  ), [data]);

  return (
    <div className="w-full">
      <PageHeader
        title="Dashboard de Atención"
        subtitle="Visión integral de la performance de soporte"
      />

      <section className="mx-auto mt-6 w-full max-w-[1400px] space-y-6">
        <div className="grid grid-cols-12 gap-4">
          {kpis.map(kpi => (
            <article
              key={kpi.label}
              className="col-span-12 rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.98)] p-5 text-[rgb(var(--fg))] shadow-xl sm:col-span-6 lg:col-span-3"
            >
              <h3 className="text-sm font-semibold text-[rgba(var(--muted),0.9)]">{kpi.label}</h3>
              <p className="mt-2 text-3xl font-semibold">{kpi.value}</p>
              <p className="mt-2 text-xs text-[rgba(var(--muted),0.9)]">{kpi.description}</p>
            </article>
          ))}
        </div>

        <div className="grid grid-cols-12 gap-6">
          <section className="col-span-12 rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.98)] p-6 shadow-xl md:col-span-6">
            <header className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Top categorías</h2>
                <p className="text-xs text-[rgba(var(--muted),0.9)]">Mayor volumen de reclamos</p>
              </div>
            </header>
            <div className="mt-4 space-y-3">
              {data?.topCategorias?.length ? (
                data.topCategorias.map(item => (
                  <button
                    key={item.categoria}
                    type="button"
                    onClick={() => onSelect?.({ type: 'categoria', data: item })}
                    className="flex w-full items-center justify-between rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-4 py-3 text-left text-sm text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--subpanel),0.9)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                  >
                    <span>{item.categoria}</span>
                    <span className="font-semibold">{item.cantidad}</span>
                  </button>
                ))
              ) : (
                <EmptyState title="Sin datos" description="Se mostrarán las categorías con más tickets una vez que haya registros." />
              )}
            </div>
          </section>

          <section className="col-span-12 rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.98)] p-6 shadow-xl md:col-span-6">
            <header className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Clientes con más tickets</h2>
                <p className="text-xs text-[rgba(var(--muted),0.9)]">Segmentación por volumen de reclamos</p>
              </div>
            </header>
            <div className="mt-4 space-y-3">
              {data?.ticketsPorCliente?.length ? (
                data.ticketsPorCliente.map(item => (
                  <button
                    key={item.clienteId}
                    type="button"
                    onClick={() => onSelect?.({ type: 'cliente', data: item })}
                    className="flex w-full items-center justify-between rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-4 py-3 text-left text-sm text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--subpanel),0.9)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                  >
                    <span>Cliente #{item.clienteId}</span>
                    <span className="font-semibold">{item.cantidad}</span>
                  </button>
                ))
              ) : (
                <EmptyState title="Sin datos" description="Sumá reclamos para ver los clientes con mayor volumen." />
              )}
            </div>
          </section>
        </div>
      </section>

      <StickyActions>
        <div className="flex w-full items-center justify-between gap-4 text-xs text-[rgba(var(--muted),0.9)]">
          <span>
            Filtros activos: {filters?.desde ? `desde ${filters.desde}` : 'sin fecha inicial'} · {filters?.hasta ? `hasta ${filters.hasta}` : 'sin fecha final'}
          </span>
          <button
            type="button"
            onClick={onChangeFilters}
            className="rounded-xl border border-[rgba(var(--border),0.6)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--subpanel),0.6)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Ajustar filtros
          </button>
        </div>
      </StickyActions>
    </div>
  );
}
